import { FC, useRef, useState } from 'react';
import { DocumentIcon, DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import { useUploadMarkScheme } from '@/api/useUploadMarkScheme';

interface UploadMarkSchemeButtonProps {
  pastPaperId: string;
  pdfUrl: string;
}

const UploadMarkSchemeButton: FC<UploadMarkSchemeButtonProps> = ({ pastPaperId, pdfUrl }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const { mutate: uploadMarkScheme, isPending } = useUploadMarkScheme();

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
    }
  };

  const handleUpload = () => {
    if (!file) return;

    uploadMarkScheme(
      { pastPaperId, pdfUrl, file },
      {
        onSuccess: () => {
          setFile(null);
          setIsOpen(false);
        },
      }
    );
  };

  const handleClose = () => {
    if (!isPending) {
      setFile(null);
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 text-gray-700 font-medium rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors"
      >
        <DocumentArrowUpIcon className="w-5 h-5" />
        Upload Mark Scheme
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={isPending}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Header */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload Mark Scheme</h2>
            <p className="text-sm text-gray-500 mb-6">
              Upload the mark scheme PDF to extract marking criteria for each question.
            </p>

            {/* File input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="hidden"
              disabled={isPending}
            />

            {/* Upload area */}
            <div
              onClick={isPending ? undefined : handleFileClick}
              className={`border-2 border-dashed rounded-xl p-8 transition-colors ${
                isPending
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-300 hover:border-gray-400 cursor-pointer'
              }`}
            >
              <div className="flex flex-col items-center gap-3">
                <DocumentIcon className="w-10 h-10 text-gray-400" />
                {file ? (
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">{file.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <>
                    <button type="button" className="text-[#C87941] hover:underline font-medium">
                      Click to upload
                    </button>
                    <span className="text-sm text-gray-500">PDF only</span>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="flex-1 px-4 py-2.5 text-gray-700 font-medium rounded-xl border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <LoadingButton
                loading={isPending}
                onClick={handleUpload}
                disabled={!file}
                className="flex-1 bg-[#0f172a] hover:bg-[#1e293b] text-white border-none"
              >
                {isPending ? 'Processing...' : 'Upload & Process'}
              </LoadingButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadMarkSchemeButton;
